import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { ApiClientError, tokenStore, type AuthClaims } from '@/lib/http-core';
import { Button, Input, InputOTP, Skeleton } from '@/components/ui';
import type { TwoFactorVerifyResponse } from '@/lib/api-admin';
import { apiClient } from '@/lib/apiClient';
import { safeNextPath } from '@/lib/rbacGuard';
import { otpSchema, type OtpFormValues } from '../schemas/otpSchema';
import { TwoFactorEnrollStep } from '../components/TwoFactorEnrollStep';
import {
  useTwoFactorEnrollMutation,
  useBackupCodeLoginMutation,
  usePhoneChallengeRequestMutation,
  usePhoneChallengeVerifyMutation,
} from '../hooks/useTwoFactorMutations';

const RESEND_COOLDOWN_SECONDS = 60;

const buildClaimsFromToken = (token: string): AuthClaims | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = parts[1] ?? '';
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    const json =
      typeof atob === 'function'
        ? atob(padded + pad)
        : Buffer.from(padded + pad, 'base64').toString('binary');
    const decoded = JSON.parse(json) as {
      sub?: string;
      roles?: string[];
      twoFactorRequired?: boolean;
      exp?: number;
    };
    return {
      sub: decoded.sub ?? '',
      roles: decoded.roles ?? [],
      twoFactorRequired: false,
      exp: decoded.exp ?? 0,
    } satisfies AuthClaims;
  } catch {
    return null;
  }
};

export const OtpChallengePage = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // QA-628 로그인 전에 열려던 화면(메일 [자세히 보기] 등) — 인증을 마치면 그리로 간다.
  const afterLogin = safeNextPath(params.get('next')) ?? '/dashboard';
  const challenge = useMemo(() => params.get('challenge') ?? '', [params]);
  const enrollRequired = useMemo(() => params.get('enroll') === '1', [params]);
  const loginId = useMemo(() => params.get('loginId') ?? '', [params]);
  // twoFactorMethod=PHONE 이면 SMS 챌린지(phone/request+verify), 그 외(TOTP/null)는 OTP verify.
  const isPhoneMethod = useMemo(() => params.get('method') === 'PHONE', [params]);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [mode, setMode] = useState<'otp' | 'backup'>('otp');
  const [backupCode, setBackupCode] = useState('');
  const [backupError, setBackupError] = useState<string | null>(null);

  const enrollMutation = useTwoFactorEnrollMutation();
  const backupCodeMutation = useBackupCodeLoginMutation();
  const phoneRequestMutation = usePhoneChallengeRequestMutation();
  const phoneVerifyMutation = usePhoneChallengeVerifyMutation();
  const enrollStartedRef = useRef(false);
  const phoneRequestedRef = useRef(false);

  const form = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: { code: '' },
  });

  useEffect(() => {
    if (!challenge) {
      navigate('/login');
    }
  }, [challenge, navigate]);

  // 강제 enrollment: challenge 토큰 확보 후 enroll 1회 호출해 secret/backup code 노출.
  useEffect(() => {
    if (!challenge || !enrollRequired || enrollStartedRef.current) return;
    enrollStartedRef.current = true;
    enrollMutation.mutate();
  }, [challenge, enrollRequired, enrollMutation]);

  // PHONE 방식: challenge 확보 시 SMS 코드 1회 자동 발송.
  useEffect(() => {
    if (!challenge || !isPhoneMethod || enrollRequired || phoneRequestedRef.current) return;
    phoneRequestedRef.current = true;
    phoneRequestMutation.mutate();
  }, [challenge, isPhoneMethod, enrollRequired, phoneRequestMutation]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const completeLogin = (accessToken: string) => {
    tokenStore.set(accessToken, buildClaimsFromToken(accessToken));
    navigate(afterLogin);
  };

  /** verify 계열 공통 에러 코드 처리. 처리했으면 true. */
  const handleVerifyErrorCode = (code: string): boolean => {
    if (code === 'USER_2FA_INVALID' || code === 'USER_PHONE_2FA_CODE_INVALID') {
      setSubmitError(t('errors.otpInvalid'));
      form.setValue('code', '');
      return true;
    }
    if (
      code === 'USER_REFRESH_TOKEN_EXPIRED' ||
      code === 'USER_2FA_CHALLENGE_EXPIRED' ||
      code === 'USER_PHONE_2FA_CODE_EXPIRED'
    ) {
      tokenStore.clear();
      navigate('/login');
      return true;
    }
    if (code === 'USER_ACCOUNT_LOCKED') {
      setSubmitError(t('errors.accountLocked'));
      return true;
    }
    if (code === 'USER_2FA_NOT_ENROLLED') {
      setSubmitError(t('errors.otpNotEnrolled'));
      return true;
    }
    return false;
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    // PHONE 방식은 phone/verify, TOTP 방식은 2fa/verify.
    if (isPhoneMethod) {
      try {
        const verified = await phoneVerifyMutation.mutateAsync({ code: values.code });
        completeLogin(verified.accessToken);
      } catch (error: unknown) {
        const code = error instanceof Error ? error.message : 'UNKNOWN';
        if (!handleVerifyErrorCode(code)) setSubmitError(t('errors.network'));
      }
      return;
    }
    try {
      const [data] = await apiClient.request('post', '/admin/v1/auth/2fa/verify', {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: { code: values.code },
      });
      const verified = data as TwoFactorVerifyResponse;
      completeLogin(verified.accessToken);
    } catch (error: unknown) {
      if (error instanceof ApiClientError) {
        if (!handleVerifyErrorCode(error.code)) {
          setSubmitError(t(`errors.${error.code}`, { defaultValue: error.message }));
        }
        return;
      }
      setSubmitError(t('errors.network'));
    }
  });

  const handleResend = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    if (isPhoneMethod) phoneRequestMutation.mutate();
  };

  const handleBackupSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setBackupError(null);
    const trimmed = backupCode.trim();
    if (trimmed === '') {
      setBackupError(t('errors.otpInvalid'));
      return;
    }
    // be-user H-3: 인증 불필요, body {loginId, backupCode}. loginId 없으면 재로그인 유도.
    if (!loginId) {
      tokenStore.clear();
      navigate('/login');
      return;
    }
    try {
      const verified = await backupCodeMutation.mutateAsync({ loginId, backupCode: trimmed });
      tokenStore.set(verified.accessToken, buildClaimsFromToken(verified.accessToken));
      navigate(afterLogin);
    } catch (error: unknown) {
      // be-user H-3 에러: USER_INVALID_CREDENTIALS/USER_2FA_INVALID(코드불일치),
      // USER_2FA_NOT_ENROLLED(403), USER_ACCOUNT_LOCKED(423), 만료→재로그인.
      const code = error instanceof Error ? error.message : 'UNKNOWN';
      if (code === 'USER_2FA_CHALLENGE_EXPIRED' || code === 'USER_REFRESH_TOKEN_EXPIRED') {
        tokenStore.clear();
        navigate('/login');
        return;
      }
      if (code === 'USER_ACCOUNT_LOCKED') {
        setBackupError(t('errors.accountLocked'));
        return;
      }
      if (code === 'USER_2FA_NOT_ENROLLED') {
        setBackupError(t('errors.otpNotEnrolled'));
        return;
      }
      setBackupError(t('errors.otpInvalid'));
      setBackupCode('');
    }
  };

  const switchMode = (next: 'otp' | 'backup') => {
    setMode(next);
    setSubmitError(null);
    setBackupError(null);
  };

  return (
    <main
      className="grid min-h-dvh place-items-center px-6"
      style={{ background: 'var(--surface-1)' }}
    >
      <section
        className="w-full max-w-[400px] rounded-lg p-8"
        style={{
          background: 'var(--surface-raised)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <header className="mb-6">
          <h1
            className="text-2xl font-bold text-[color:var(--content-strong)]"
            style={{ fontFamily: 'var(--font-sans)' }}
          >
            {enrollRequired
              ? t('otp.enrollTitle')
              : mode === 'backup'
                ? t('otp.backupTitle')
                : isPhoneMethod
                  ? t('otp.phoneTitle')
                  : t('otp.title')}
          </h1>
          <p className="mt-1 text-sm text-[color:var(--content-muted)]">
            {enrollRequired
              ? t('otp.enrollSubtitle')
              : mode === 'backup'
                ? t('otp.backupSubtitle')
                : isPhoneMethod
                  ? t('otp.phoneSubtitle')
                  : t('otp.subtitle')}
          </p>
        </header>

        {enrollRequired ? (
          <div className="mb-6">
            {enrollMutation.isPending || enrollMutation.isIdle ? (
              <div className="space-y-2" role="status" aria-live="polite">
                <Skeleton className="h-[44px] w-full" />
                <Skeleton className="h-[120px] w-full" />
              </div>
            ) : enrollMutation.isError ? (
              <div
                className="flex flex-col items-start gap-2 rounded-md p-3 text-sm"
                role="alert"
                style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
              >
                <span>{t('otp.enrollFailed')}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => enrollMutation.mutate()}
                >
                  {t('otp.enrollRetry')}
                </Button>
              </div>
            ) : enrollMutation.data ? (
              <TwoFactorEnrollStep data={enrollMutation.data} />
            ) : null}
          </div>
        ) : null}

        {mode === 'otp' ? (
        <form className="space-y-5" onSubmit={onSubmit} noValidate data-testid="admin-otp-form">
          <div data-testid="admin-otp-input">
            <Controller
              name="code"
              control={form.control}
              render={({ field }) => (
                <InputOTP
                  value={field.value}
                  onChange={field.onChange}
                  aria-label={t('otp.codeLabel')}
                />
              )}
            />
          </div>
          {form.formState.errors.code ? (
            <p className="text-xs" style={{ color: 'var(--signal-danger)' }} role="alert">
              {t(form.formState.errors.code.message ?? 'errors.unknown')}
            </p>
          ) : null}

          {submitError ? (
            <div
              className="rounded-md p-3 text-sm"
              role="alert"
              style={{
                background: 'var(--status-warn-bg)',
                color: 'var(--status-warn-fg)',
              }}
            >
              {submitError}
            </div>
          ) : null}

          <Button
            type="submit"
            variant="cta"
            className="w-full"
            style={{ height: '44px' }}
            disabled={
              form.formState.isSubmitting || (enrollRequired && !enrollMutation.isSuccess)
            }
            data-testid="admin-otp-submit"
          >
            {form.formState.isSubmitting ? t('otp.submitting') : t('otp.submit')}
          </Button>

          {enrollRequired ? null : (
          <div className="flex items-center justify-between text-sm">
            <span className="text-[color:var(--content-muted)]">
              {cooldown > 0
                ? t('otp.resendCountdown', { seconds: cooldown })
                : t('otp.resendAvailable')}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={cooldown > 0}
            >
              {t('otp.resend')}
            </Button>
          </div>
          )}
        </form>
        ) : (
          <form
            className="space-y-5"
            onSubmit={handleBackupSubmit}
            noValidate
            data-testid="admin-backup-form"
          >
            <Input
              type="text"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value)}
              placeholder={t('otp.backupCodePlaceholder')}
              autoComplete="one-time-code"
              aria-label={t('otp.backupCodeLabel')}
              aria-invalid={backupError ? true : undefined}
              data-testid="admin-backup-input"
            />

            {backupError ? (
              <div
                className="rounded-md p-3 text-sm"
                role="alert"
                style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
              >
                {backupError}
              </div>
            ) : null}

            <Button
              type="submit"
              variant="cta"
              className="w-full"
              style={{ height: '44px' }}
              disabled={backupCodeMutation.isPending}
              data-testid="admin-backup-submit"
            >
              {backupCodeMutation.isPending
                ? t('otp.backupSubmitting')
                : t('otp.backupSubmit')}
            </Button>
          </form>
        )}

        {enrollRequired ? null : (
          <div className="mt-4 text-center text-sm">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => switchMode(mode === 'otp' ? 'backup' : 'otp')}
              data-testid="admin-backup-toggle"
            >
              {mode === 'otp' ? t('otp.useBackupCode') : t('otp.useOtpCode')}
            </Button>
          </div>
        )}
      </section>
    </main>
  );
};
